import React, { useEffect, useMemo, useState } from 'react';
import { YearContainer } from "@features/holidays/pages/user-annual-holidays/components/YearContainer";
import { useDispatch, useSelector } from "react-redux";
import { selectConfig } from "@shared/store/config.duck";
import moment from "moment";
import DateFlicker, { DateScale } from '@shared/ui/molecules/DateFlicker';
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import styled from "styled-components";
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { Sidebar } from '@shared/ui/atoms/Sidebar';
import { CompanyHolidaysList } from "./components/CompanyHolidaysList";
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import {
  CompanyHoliday,
  CompanyHolidayOverview,
  getCompanyHolidayList
} from "@features/holidays/services/CompanyHolidayService";
import { selectCalendarDate } from "@shared/store/dashboard.duck";
import AccessRequired from "../components/AccessRequired";
import { redirectTo, selectCurrentUser } from "@shared/store/auth.duck";
import { canChangeCompanyHolidays } from "@shared/utils/AccessControl";
import { useTranslation } from "react-i18next";

export function CompanyHolidaysPage(props: Props) {
  const config = useSelector(selectConfig);
  const calendarDate = useSelector(selectCalendarDate);
  const currentUser = useSelector(selectCurrentUser);
  const [date, setDate] = useState(calendarDate ?? moment());
  const [companyHolidays, setCompanyHolidays] = useState<CompanyHolidayOverview | undefined>(undefined);
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const canEdit = useMemo(() => canChangeCompanyHolidays(currentUser), [currentUser]);

  const holidayYearStartDate = useMemo(() => {
    return config.getHolidayYearStartDate(date.clone());
  }, [date, config]);

  useEffect(() => {
    getCompanyHolidayList(date.year()).then(resp => setCompanyHolidays(resp));
  }, [date]);

  const onCompanyHolidayClick = (clickedCompanyHoliday: CompanyHoliday) => {
    dispatch(redirectTo(`/manage/company-holidays/view/${clickedCompanyHoliday.id}`));
  }

  return (
    <Container>
      <PageHeader>
        <GoBackButton url={"/manage"} />
        <HeadlineLarge style={{marginBottom: 24}}>{t('company-holiday.title')}</HeadlineLarge>
        <AccessRequired visible={!canEdit} companyAdminsRequired={true} />
        <SpaceBetweenRow>
          <BodyRegular className={"companyHolidayPageHeader__description"}>{t('company-holiday.description')}</BodyRegular>
          <DateFlicker date={date.clone()} dateScale={DateScale.Year} updateGlobalDate={false} onChange={setDate} />
        </SpaceBetweenRow>
      </PageHeader>

      <Content>
        <Sidebar>
          <CompanyHolidaysList disabled={!canEdit} activeDate={holidayYearStartDate} companyHolidays={companyHolidays} />
        </Sidebar>
        <YearContainer holidaysBooked={[]}
                       colourOverride={'green'}
                       startDate={holidayYearStartDate}
                       companyHolidays={companyHolidays?.companyHolidays ?? []}
                       onCompanyHolidayClick={onCompanyHolidayClick}
                       nonWorkingDays={[]}
                       customNonWorkingDays={[]}
                       customLeaveDays={[]} />
      </Content>
    </Container>
  )
}

interface Props {
}


const Container = styled.div`
  max-width: 100%;
  background-color: white;
`

const SpaceBetweenRow = styled.div`
  display: flex;
  flex-direction: column;
  @media (${tablet}) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  @media (${tablet}) {
    flex-direction: row;
    justify-content: space-between;
    margin-left: 32px;
  }
`

const PageHeader = styled.div`
  padding: 32px 32px 48px 32px;
  .companyHolidayPageHeader__description {
    max-width: 565px;
  }
  .weekFlicker {
    width: 100%;
    @media (${tablet}) {
      width: unset;
      min-width: 300px;
    }
  }
`
